import { MIN_SPACE_DIM_FEET } from 'data/constants';
import { PositionAndDimensionsType, PositionType, SpaceType } from 'data/types';
import polygonClipping, { Polygon as ClipPolygon } from 'polygon-clipping';
import { polygonAreaAbs } from 'utils/space-gap-fill';
import {
  boundingBoxOfVertices,
  getSpaceVertices,
  isRectangleBboxVertices,
  polygonsOverlapStrict,
  rectToAbsolutePolygon
} from 'utils/space-polygon';

const MIN_REMNANT_AREA = MIN_SPACE_DIM_FEET * MIN_SPACE_DIM_FEET;

export type LeftoverGeometry = {
  position: PositionType;
  width?: number;
  length?: number;
  vertices: PositionType[];
  squareFootage: number;
};

export type CarveAutoLayoutSuccess = {
  ok: true;
  leftover: LeftoverGeometry | null;
  extras: LeftoverGeometry[];
};

export type CarveAutoLayoutFailure = {
  ok: false;
  reason: 'no-overlap' | 'hole' | 'too-small';
};

export type CarveAutoLayoutResult =
  | CarveAutoLayoutSuccess
  | CarveAutoLayoutFailure;

export type LeftoverCarvePlan = {
  leftover: SpaceType;
  leftoverGeometry: LeftoverGeometry | null;
  extras: LeftoverGeometry[];
};

export function polygonToClipping(vertices: PositionType[]): ClipPolygon {
  const ring = vertices.map((v) => [v.x, v.y] as [number, number]);
  return [[...ring, ring[0]]];
}

export function spaceAbsolutePolygon(space: SpaceType): PositionType[] | null {
  if (!space.position) return null;
  const relative = getSpaceVertices(space);
  if (relative.length < 3) return null;
  return relative.map((vertex) => ({
    x: space.position!.x + vertex.x,
    y: space.position!.y + vertex.y
  }));
}

export function geometryFromAbsolutePolygon(
  absVertices: PositionType[]
): LeftoverGeometry | null {
  if (absVertices.length < 3) return null;
  const bb = boundingBoxOfVertices(absVertices);
  if (bb.width < MIN_SPACE_DIM_FEET || bb.height < MIN_SPACE_DIM_FEET) {
    return null;
  }
  const relative = absVertices.map((vertex) => ({
    x: vertex.x - bb.minX,
    y: vertex.y - bb.minY
  }));
  const isRectangle = isRectangleBboxVertices(relative, bb.width, bb.height);
  return {
    position: { x: bb.minX, y: bb.minY },
    width: isRectangle ? bb.width : undefined,
    length: isRectangle ? bb.height : undefined,
    vertices: relative,
    squareFootage: Math.ceil(polygonAreaAbs(absVertices))
  };
}

export function clippingOuters(mp: polygonClipping.MultiPolygon): {
  hasHole: boolean;
  polygons: PositionType[][];
} {
  let hasHole = false;
  const polygons: PositionType[][] = [];
  for (const poly of mp) {
    if (poly.length > 1) {
      hasHole = true;
    }
    const outer = poly[0];
    if (outer && outer.length >= 4) {
      const points = outer.slice(0, -1).map(([x, y]) => ({ x, y }));
      if (points.length >= 3) {
        polygons.push(points);
      }
    }
  }
  return { hasHole, polygons };
}

export function carveAutoLayoutSpace(
  leftover: SpaceType,
  incoming: PositionAndDimensionsType
): CarveAutoLayoutResult {
  const leftoverPolygon = spaceAbsolutePolygon(leftover);
  if (!leftoverPolygon) {
    return { ok: false, reason: 'no-overlap' };
  }
  const incomingPolygon = rectToAbsolutePolygon({
    x: incoming.x,
    y: incoming.y,
    width: incoming.width,
    height: incoming.height
  });
  if (!polygonsOverlapStrict(leftoverPolygon, incomingPolygon)) {
    return { ok: false, reason: 'no-overlap' };
  }

  const difference = polygonClipping.difference(
    polygonToClipping(leftoverPolygon),
    polygonToClipping(incomingPolygon)
  );
  const { hasHole, polygons } = clippingOuters(difference);
  if (hasHole) {
    return { ok: false, reason: 'hole' };
  }

  const remnants = polygons
    .map((polygon) => ({
      polygon,
      area: polygonAreaAbs(polygon)
    }))
    .filter((entry) => entry.area >= MIN_REMNANT_AREA)
    .sort((a, b) => b.area - a.area);

  if (remnants.length === 0) {
    return { ok: true, leftover: null, extras: [] };
  }

  const leftoverGeometry = geometryFromAbsolutePolygon(remnants[0].polygon);
  const extras = remnants
    .slice(leftoverGeometry ? 1 : 0)
    .map((entry) => geometryFromAbsolutePolygon(entry.polygon))
    .filter((geometry): geometry is LeftoverGeometry => geometry !== null);

  return {
    ok: true,
    leftover: leftoverGeometry,
    extras
  };
}
